import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconButton, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Spinner } from 'react-bootstrap';
import ChartContainer from './ChartContainer';
import { chartTypeEnum } from '../constants';
import ErrorComponent from './ErrorComponent';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DownloadIcon from '@mui/icons-material/Download';
import EnlargedChartModal from '../EnlargedChartModal';
import NoDataComponent from './NoDataComponent';
import ChartContent from './ChartContent';
import downloadData from '../../../utils/AnalyticsExportUtils';
import CalendarIconDatePicker from '../../../shared/UIComponents/CalendarIconDatePicker';
import dayjs from '../../../utils/dayjs';

const useStyles = makeStyles(() => ({
  tableContainer: {
    maxHeight: '100vh',
    height: '100%',
    overflow: 'scroll',
    border: 'solid black',
    borderWidth: '1px',
    borderRadius: '1px',
  },
  text: {
    paddingLeft: '5%',
    paddingTop: '1vh',
    color: '#ffffff',
    fontSize: '100%',
  },
  tableHeader: {
    backgroundColor: '#002145',
    background: '#002145',
    height: '5vh',
    textAlign: 'left',
  },
  fullScreenButton: {
    position: 'absolute',
    top: '10%',
    right: '1%',
    zIndex: '1',
  },
  cameraButton: {
    position: 'absolute',
    top: '10%',
    right: '7%',
    zIndex: '1000',
  },
  downloadButton: {
    position: 'absolute',
    top: '10%',
    right: '13%',
    zIndex: '1000',
  },
  gridItem: {
    position: 'absolute',
  },
  content: {
    position: 'relative',
  },
}));

function ChartComponent({ chartDataIn, query }) {
  //component for rending a chart/numbox
  const { title, type, networkRequest, enlargedViewRequest, isYearlyChart, queryKey } = chartDataIn;
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const now = dayjs().endOf('day');
  const defaultDateQuery = `year=${now.format('YYYY')}`;

  const [dateQuery, setDateQuery] = useState(defaultDateQuery);
  const [date, setDate] = useState(now);

  const changeYear = (newDate) => {
    setDate(newDate);
    setDateQuery(`year=${newDate.format('YYYY')}`);
  };

  const supportedDataExports = new Set([
    chartTypeEnum.ChartJSBar,
    chartTypeEnum.ChartJSDoughnut,
    chartTypeEnum.ChartJSStackedBar,
    chartTypeEnum.line,
    chartTypeEnum.PlotlyMonthLine,
    chartTypeEnum.topHits,
    chartTypeEnum.wordCloud,
    chartTypeEnum.numberBox,
  ]);

  const resolvedQuery = isYearlyChart ? dateQuery : query;
  const baseQueryKey = queryKey ?? title;

  const chartQuery = useQuery({
    queryKey: ['analytics-chart', baseQueryKey, isYearlyChart ? 'year' : 'range', resolvedQuery],
    queryFn: () => networkRequest(resolvedQuery),
    enabled: Boolean(resolvedQuery),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const enlargedViewQuery = useQuery({
    queryKey: ['analytics-chart', `${baseQueryKey}-enlarged`, isYearlyChart ? 'year' : 'range', resolvedQuery],
    queryFn: () => enlargedViewRequest(resolvedQuery),
    enabled: Boolean(enlargedViewRequest) && Boolean(resolvedQuery),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const chartData = chartQuery.isLoading ? 'loading' : chartQuery.data ?? 'loading';
  const enlargedViewData = enlargedViewQuery.isLoading ? 'loading' : enlargedViewQuery.data ?? 'loading';

  let openButtonHandler = () => {
    setOpen(true);
  };

  const EnlargedChartComponent = (open, enlargedViewRequest, chartDataIn, enlargedViewData, chartData) => (
    <EnlargedChartModal
      open={open}
      onClose={() => setOpen(false)}
      display={
        enlargedViewRequest ? (
          <ChartContent
            chartDataParam={{
              ...chartDataIn,
              networkRequest: enlargedViewRequest,
              enlargedViewRequest: enlargedViewData,
              chartData: enlargedViewData,
            }}
            query={isYearlyChart ? dateQuery : query}
            open={open}
          ></ChartContent>
        ) : (
          <ChartContent
            chartDataParam={{ ...chartDataIn, chartData: chartData }}
            query={isYearlyChart ? dateQuery : query}
            open={open}
          ></ChartContent>
        )
      }
    ></EnlargedChartModal>
  );

  if (chartQuery.isError) {
    return <ErrorComponent message={chartQuery.error?.message ?? 'Failed to load chart data.'}></ErrorComponent>;
  } else if (chartData.error === 1) {
    return <ErrorComponent message={chartData.message}></ErrorComponent>;
  } else if (chartData !== 'loading' && chartData === 'noData') {
    return <NoDataComponent title={title}></NoDataComponent>;
  } else if (chartData !== 'loading' && chartData !== 'noData') {
    return (
      <>
        {EnlargedChartComponent(open, enlargedViewRequest, chartDataIn, enlargedViewData, chartData)}
        <ChartContainer
          small={type === chartTypeEnum.numberBox || type === chartTypeEnum.topHits}
          large={type === chartTypeEnum.barWide}
        >
          <ChartContent
            className={classes.content}
            chartDataParam={{ ...chartDataIn, chartData: chartData }}
            query={isYearlyChart ? dateQuery : query}
          ></ChartContent>
          <IconButton className={classes.fullScreenButton} onClick={openButtonHandler}>
            <FullscreenIcon />
          </IconButton>
          {supportedDataExports.has(type) && !isYearlyChart && (
            <IconButton className={classes.downloadButton} onClick={() => downloadData(chartData, type, title)}>
              <DownloadIcon />
            </IconButton>
          )}
          {isYearlyChart && (
            <CalendarIconDatePicker value={date} onChange={(newYear) => changeYear(newYear)} title={title} />
          )}
        </ChartContainer>
      </>
    );
  } else {
    return (
      <ChartContainer
        small={type === chartTypeEnum.numberBox || type === chartTypeEnum.topHits}
        large={type === chartTypeEnum.barWide}
      >
        <div>
          <Typography>Waiting for data</Typography>
          <Spinner animation="border" />
        </div>
      </ChartContainer>
    );
  }
}

export default ChartComponent;
